import { idb } from "../../db/index.ts";
import { random, helpers } from "../../util/index.ts";
import { range } from "../../../../common/utils.ts";

const VALID_JERSEY_NUMBERS = range(1, 100).map(String);
VALID_JERSEY_NUMBERS.push("0", "00");

const weightFunction = (jerseyNumber: string) => {
	// https://old.reddit.com/r/nba/comments/4sxoi0/the_mostleast_worn_jersey_numbers/
	const frequencies: Record<string, number> = {
		"00": 29,
		0: 68,
		1: 199,
		2: 176,
		3: 283,
		4: 263,
		5: 298,
		6: 210,
		7: 284,
		8: 224,
		9: 233,
		10: 279,
		11: 341,
		12: 380,
		13: 173,
		14: 301,
		15: 304,
		16: 108,
		17: 145,
		18: 120,
		19: 87,
		20: 305,
		21: 274,
		22: 282,
		23: 214,
		24: 240,
		25: 201,
		26: 45,
		27: 48,
		28: 32,
		29: 26,
		30: 205,
		31: 168,
		32: 209,
		33: 224,
		34: 210,
		35: 166,
		36: 17,
		37: 4,
		38: 8,
		39: 4,
		40: 162,
		41: 100,
		42: 158,
		43: 99,
		44: 209,
		45: 106,
		46: 4,
		47: 5,
		48: 2,
		49: 2,
		50: 114,
		51: 39,
		52: 80,
		53: 29,
		54: 81,
		55: 90,
		56: 3,
		57: 1,
		58: 0,
		59: 0,
		60: 2,
		61: 2,
		62: 2,
		63: 1,
		64: 0,
		65: 1,
		66: 3,
		67: 1,
		68: 1,
		69: 0,
		70: 7,
		71: 3,
		72: 1,
		73: 1,
		74: 0,
		75: 0,
		76: 0,
		77: 10,
		78: 0,
		79: 0,
		80: 0,
		81: 0,
		82: 0,
		83: 1,
		84: 1,
		85: 1,
		86: 2,
		87: 0,
		88: 5,
		89: 2,
		90: 2,
		91: 2,
		92: 2,
		93: 2,
		94: 1,
		95: 0,
		96: 2,
		97: 0,
		98: 3,
		99: 6,
	};

	const frequency = frequencies[jerseyNumber];

	if (frequency === undefined || frequency === 0) {
		// Never have 0 probability
		return 0.25;
	}

	return frequency;
};

export const JERSEY_NUMBERS_BY_POSITION = false;

export const getTeammateJerseyNumbers = async (
	tid: number,
	pidsIgnore: number[],
) => {
	const pidsIgnoreSet = new Set(pidsIgnore);
	const jerseyNumbers = [];

	if (tid >= 0) {
		const teammates = (
			await idb.cache.players.indexGetAll("playersByTid", tid)
		).filter((p) => !pidsIgnoreSet.has(p.pid));
		for (const p of teammates) {
			const teamJerseyNumber = p.jerseyNumber;
			if (teamJerseyNumber) {
				jerseyNumbers.push(teamJerseyNumber);
			}
		}
	}

	return jerseyNumbers;
};

const getRetiredJerseyNumbers = async (tid: number) => {
	const t = await idb.cache.teams.get(tid);
	if (t?.retiredJerseyNumbers) {
		return t.retiredJerseyNumbers.map((row) => row.number);
	}

	return [];
};

const genJerseyNumber = async (
	p: {
		pid?: number;
		tid: number;
		jerseyNumber?: string;
		ratings: {
			pos: string;
		}[];
		stats: any[];
	},

	// When this is undefined, it'll read from the database to find what it should be. But that won't work during league creation. Also sometimes "teamJerseyNumbersInput" needs to be overwritten, like when processing trades
	teamJerseyNumbersInput?: string[],
	retiredJerseyNumbersInput?: string[],

	// When this is true, ignore current/previous jersey number
	pickRandomNumber?: boolean,

	// When all jersey numbers are retired, it will add a prefix and try again
	prefix?: number,
): Promise<string> => {
	let prevJerseyNumber;
	if (!pickRandomNumber) {
		prevJerseyNumber = helpers.getJerseyNumber(p);

		if (p.tid < 0 && prevJerseyNumber !== undefined) {
			return prevJerseyNumber;
		}
	}

	const teamJerseyNumbers =
		teamJerseyNumbersInput ??
		(await getTeammateJerseyNumbers(p.tid, p.pid !== undefined ? [p.pid] : []));
	const retiredJerseyNumbers =
		retiredJerseyNumbersInput ?? (await getRetiredJerseyNumbers(p.tid));

	let validJerseyNumbers;
	if (prefix === undefined) {
		validJerseyNumbers = VALID_JERSEY_NUMBERS;
	} else {
		validJerseyNumbers = range(0, 100).map((i) => String(prefix * 100 + i));
	}

	const candidates = validJerseyNumbers.filter(
		(jerseyNumber) =>
			!teamJerseyNumbers.includes(jerseyNumber) &&
			!retiredJerseyNumbers.includes(jerseyNumber),
	);

	if (
		prevJerseyNumber &&
		(candidates.includes(prevJerseyNumber) ||
			!validJerseyNumbers.includes(prevJerseyNumber))
	) {
		// Keep old jersey number, if it is available or if prevJerseyNumber is not a valid jersey number (must have been manually edited)
		return prevJerseyNumber;
	}

	if (candidates.length === 0) {
		// No valid jersey number left! Try again with a larger prefix (so it'll pick numbers like 1XX rather than XX, then 2XX, 3XX, etc.)
		return genJerseyNumber(
			p,
			teamJerseyNumbersInput,
			retiredJerseyNumbersInput,
			pickRandomNumber,
			(prefix ?? 0) + 1,
		);
	}

	return random.choice(candidates, weightFunction);
};

export default genJerseyNumber;
